import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { BriefcaseBusiness, Eye, Loader2, Paperclip, UserPlus } from 'lucide-react';
import { StagingAttachmentPreview, isPreviewable } from './StagingAttachmentPreview';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'customer_inquiry', label: 'Customer inquiry / RFQ' },
  { value: 'drawing', label: 'Drawing' },
  { value: 'technical', label: 'Technical document' },
  { value: 'costing_sheet', label: 'Cost sheet' },
  { value: 'other', label: 'Other' },
  { value: 'ignore', label: "Don't attach" },
];

export const CreateInquiryFromEmailDialog = ({ email, open, onOpenChange, onCreated }) => {
  const token = localStorage.getItem('access_token');
  const [plan, setPlan] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState('');
  const [newCustomer, setNewCustomer] = useState(null);
  const [categories, setCategories] = useState({});
  const [creating, setCreating] = useState(false);
  const [previewing, setPreviewing] = useState(null);

  useEffect(() => {
    if (!open || !email) { setPlan(null); setNewCustomer(null); setCustomerId(''); return; }
    const headers = { Authorization: `Bearer ${token}` };
    axios.get(`${API_URL}/staging-inbox/${email.id}/inquiry-plan`, { headers })
      .then(({ data }) => {
        setPlan(data);
        setCustomerId(data.customer?.id || data.customer_suggestions?.[0]?.id || '');
        setCategories(Object.fromEntries(data.attachments.map((item) => [item.index, item.suggested_category])));
        if (!data.customer) {
          setNewCustomer(null);
        }
      })
      .catch(() => toast.error('Could not read this email'));
    axios.get(`${API_URL}/customers`, { headers })
      .then(({ data }) => setCustomers(Array.isArray(data) ? data : (data.items || [])))
      .catch(() => setCustomers([]));
  }, [open, email, token]);

  const counts = useMemo(() => {
    const rows = (plan?.attachments || []).map((attachment) => ({
      retained: attachment.retained,
      category: categories[attachment.index] || attachment.suggested_category,
    }));
    return {
      attach: rows.filter((row) => row.category !== 'ignore' && row.retained).length,
      ignore: rows.filter((row) => row.category === 'ignore').length,
      missing: rows.filter((row) => row.category !== 'ignore' && !row.retained).length,
    };
  }, [categories, plan]);

  const create = async () => {
    if (!plan?.existing_inquiry && !customerId && !newCustomer?.name?.trim()) {
      return toast.error('Pick the customer, or create one from this sender');
    }
    setCreating(true);
    try {
      const { data } = await axios.post(`${API_URL}/staging-inbox/${email.id}/promote-to-inquiry`, {
        customer_id: newCustomer?.name?.trim() ? null : (customerId || null),
        new_customer: newCustomer?.name?.trim() ? {
          name: newCustomer.name.trim(),
          contact_person: newCustomer.contact_person || plan?.sender_name || null,
          contact_email: newCustomer.contact_email || plan?.sender_email || null,
          contact_phone: newCustomer.contact_phone || null,
        } : null,
        attachments: Object.entries(categories).map(([index, category]) => ({ index: Number(index), category })),
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(
        data.attached_to_existing
          ? `Follow-up attached to inquiry ${data.inquiry_number}`
          : `Inquiry ${data.inquiry_number} created for ${data.customer_name || ''}`,
        {
          description: `${data.documents_attached} attachment(s) filed${data.attachments_ignored ? ` · ${data.attachments_ignored} skipped` : ''}${data.attachments_missing ? ` · ${data.attachments_missing} not retained` : ''}. Use AI Extract on the Documents tab to read the items.`,
        },
      );
      onOpenChange(false);
      onCreated(data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not create the inquiry');
    } finally { setCreating(false); }
  };

  if (!email) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="create-inquiry-from-email-dialog">
        <DialogHeader>
          <DialogTitle>Create inquiry from email</DialogTitle>
          <DialogDescription>{email.subject || 'No subject'} — from {email.sender_email}</DialogDescription>
        </DialogHeader>

        {!plan ? <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin" /></div> : (
          <div className="space-y-5">
            {plan.existing_inquiry && (
              <p className="border border-blue-300 bg-blue-50 p-3 text-sm text-blue-900" data-testid="email-thread-notice">
                This is a reply on the thread that created inquiry {plan.existing_inquiry.inquiry_number} — the email and
                the attachments you keep below will be added to that inquiry instead of a new one.
              </p>
            )}

            {!plan.existing_inquiry && (
              <div className="space-y-2">
                <Label>Customer</Label>
                {plan.customer ? (
                  <p className="text-sm" data-testid="email-customer-matched">
                    Matched from the sender: <span className="font-semibold">{plan.customer.name}</span>
                  </p>
                ) : (
                  <p className="text-sm text-amber-800" data-testid="email-customer-unmatched">
                    {plan.sender_email} is not in Customer Master — pick the customer or create one.
                  </p>
                )}
                {!newCustomer && (
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <select className="h-10 flex-1 border bg-white px-2 text-sm" value={customerId}
                      onChange={(event) => setCustomerId(event.target.value)} data-testid="email-customer-select">
                      <option value="">— Select customer —</option>
                      {customers.map((customer) => (
                        <option key={customer.id} value={customer.id}>
                          {customer.customer_code ? `${customer.customer_code} · ` : ''}{customer.name}
                        </option>
                      ))}
                    </select>
                    <Button variant="outline" onClick={() => setNewCustomer({
                      name: plan.sender_name || '', contact_person: plan.sender_name || '',
                      contact_email: plan.sender_email || '', contact_phone: '',
                    })} data-testid="email-new-customer-button">
                      <UserPlus className="mr-2 h-4 w-4" /> New customer
                    </Button>
                  </div>
                )}
                {newCustomer && (
                  <div className="grid gap-2 border p-3 sm:grid-cols-2" data-testid="email-new-customer-form">
                    <div className="space-y-1"><Label className="text-xs">Customer name *</Label>
                      <Input value={newCustomer.name} onChange={(event) => setNewCustomer({ ...newCustomer, name: event.target.value })}
                        data-testid="email-new-customer-name" /></div>
                    <div className="space-y-1"><Label className="text-xs">Contact person</Label>
                      <Input value={newCustomer.contact_person} onChange={(event) => setNewCustomer({ ...newCustomer, contact_person: event.target.value })}
                        data-testid="email-new-customer-contact" /></div>
                    <div className="space-y-1"><Label className="text-xs">Email</Label>
                      <Input value={newCustomer.contact_email} onChange={(event) => setNewCustomer({ ...newCustomer, contact_email: event.target.value })}
                        data-testid="email-new-customer-email" /></div>
                    <div className="space-y-1"><Label className="text-xs">Phone</Label>
                      <Input value={newCustomer.contact_phone} onChange={(event) => setNewCustomer({ ...newCustomer, contact_phone: event.target.value })}
                        data-testid="email-new-customer-phone" /></div>
                    <Button variant="ghost" className="sm:col-span-2 justify-start" onClick={() => setNewCustomer(null)}
                      data-testid="email-new-customer-cancel">Use an existing customer instead</Button>
                  </div>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label className="flex items-center gap-2"><Paperclip className="h-4 w-4" /> Attachments — file each one</Label>
              {plan.attachments.length === 0 ? (
                <p className="text-sm text-muted-foreground" data-testid="email-no-attachments">This email has no attachments.</p>
              ) : (
                <div className="max-h-[240px] space-y-2 overflow-y-auto border p-3" data-testid="email-attachment-choices">
                  {plan.attachments.map((attachment) => (
                    <div key={attachment.index} className="flex flex-col gap-2 border-b pb-2 last:border-0 sm:flex-row sm:items-center"
                      data-testid={`email-attachment-${attachment.index}`}>
                      <span className="flex-1 truncate text-sm">{attachment.filename}
                        <span className="ml-2 text-xs text-muted-foreground">{Math.round((attachment.size || 0) / 1024)} KB</span>
                      </span>
                      {!attachment.retained && (
                        <Badge variant="outline" className="border-amber-300 bg-amber-50 text-xs text-amber-800"
                          data-testid={`email-attachment-missing-${attachment.index}`}>not retained</Badge>
                      )}
                      {attachment.retained && isPreviewable(attachment.filename) && (
                        <Button size="sm" variant="outline" onClick={() => setPreviewing(attachment)}
                          data-testid={`email-attachment-preview-${attachment.index}`}>
                          <Eye className="mr-1 h-4 w-4" /> Preview
                        </Button>
                      )}
                      <select className="h-9 border bg-white px-2 text-xs sm:w-56" value={categories[attachment.index] || 'other'}
                        onChange={(event) => setCategories({ ...categories, [attachment.index]: event.target.value })}
                        data-testid={`email-attachment-category-${attachment.index}`}>
                        {CATEGORIES.map((category) => <option key={category.value} value={category.value}>{category.label}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-muted-foreground" data-testid="email-attachment-summary">
                {counts.attach} will be filed on the inquiry{counts.ignore ? `, ${counts.ignore} skipped` : ''}
                {counts.missing ? `, ${counts.missing} cannot be filed until re-fetched from the mailbox` : ''}. Read the
                items afterwards with AI Extract on the inquiry's Documents tab.
              </p>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="create-inquiry-cancel-button">Cancel</Button>
          <Button onClick={create} disabled={creating || !plan} data-testid="create-inquiry-confirm-button">
            {creating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <BriefcaseBusiness className="mr-2 h-4 w-4" />}
            {plan?.existing_inquiry ? 'Attach to inquiry' : 'Create inquiry'}
          </Button>
        </DialogFooter>

        <StagingAttachmentPreview
          emailId={email.id}
          attachment={previewing}
          open={Boolean(previewing)}
          onOpenChange={(value) => !value && setPreviewing(null)}
        />
      </DialogContent>
    </Dialog>
  );
};
